import AuthorizationService from "../services/authorizationService";
import RepositoryBase from "./repositoryBase";
import Exemption from "models/exemption";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import EshopError from "../models/eshopError";
import { plainToClass } from "class-transformer";
import { toJS } from "mobx";
import IsdsResponse from "models/isdsResponse";
import { PersistedBase64 } from "../models/notice";
import { parseFileNameFromHeaderResponse, persistedBlobToISDSFormat } from "../utils/fileUtils";
import EventBus from "../services/eventBus";
import ExemptionApplicationProductionResponse from "entities/exemptionApplicationProductionResponse";
import BlobWithFileName from "models/blobWithFileName";

export const POST_EXEMPTION_REQUEST_FAILURE = "POST_EXEMPTION_REQUEST_FAILURE";
export const POST_EXEMPTION_PREVIEW_REQUEST_FAILURE = "POST_EXEMPTION_PREVIEW_REQUEST_FAILURE";
const POST_EXEMPTION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE = "POST_EXEMPTION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE";

export default class ExemptionRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  createExemptionRequestObj(exemption: Exemption) {
    const notice = toJS(exemption);

    if (notice.vehicleOperator) {
      if (notice.vehicleOperator.isLegalPerson) {
        notice.vehicleOperator.naturalPerson = undefined;
      } else {
        notice.vehicleOperator.legalPerson = undefined;
      }
    }

    if (
      notice.vehicleOperator?.isLegalPerson === false &&
      notice.vehicleOperator?.naturalPerson?.isRepresentedByProxy === false
    ) {
      notice.vehicleOperator.naturalPerson = undefined;
    }

    if (
      (!notice.vehicleOperator?.legalPerson?.companyAddress ||
        !notice.vehicleOperator?.legalPerson?.companyRegistrationNumber ||
        !notice.vehicleOperator?.legalPerson?.companyName) &&
      (!notice.vehicleOperator?.naturalPerson?.mailingAddress ||
        !notice.vehicleOperator?.naturalPerson?.birthDate ||
        !notice.vehicleOperator?.naturalPerson?.fullName)
    ) {
      notice.vehicleOperator = undefined;
    }

    return notice;
  }

  createExemption(exemption: Exemption): Promise<EshopError | BlobWithFileName> {
    const notice = this.createExemptionRequestObj(exemption);

    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one("exemptions/applications")
        .clearPost(notice, undefined, undefined, undefined, "application/pdf")
        .then(async (response: any) => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          const fileName = parseFileNameFromHeaderResponse(response.headers.get("Content-Disposition"));
          const blob = await response.blob();

          return { blob: blob, fileName: fileName ?? "download" } as BlobWithFileName;
        })
        .catch(() => {
          throw new EshopError(POST_EXEMPTION_REQUEST_FAILURE);
        })
    );
  }

  getProduction(exemption: Exemption): Promise<ExemptionApplicationProductionResponse | EshopError> {
    const notice = this.createExemptionRequestObj(exemption);

    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one("exemptions/application_preview")
        .post(notice)
        .then(response => {
          return plainToClass(ExemptionApplicationProductionResponse, response);
        })
        .catch(() => new EshopError(POST_EXEMPTION_PREVIEW_REQUEST_FAILURE))
    );
  }

  uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    const data = new FormData();
    files.forEach(persistedBlob => data.append("documentFiles", persistedBlobToISDSFormat(persistedBlob)));

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one("exemptions/applications/documents/isds")
        .postData(data, ISDSSessionId)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(IsdsResponse, (await response.json()) as IsdsResponse);
        })
        .catch(() => {
          throw new EshopError(POST_EXEMPTION_APPLICATIONS_DOCUMENTS_ISDS_FAILURE);
        })
    );
  }
}
